import { useCallback, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ACCEPTED_UPLOAD_MIME,
  partitionUploadFiles,
} from '@/lib/uploadConstraints'

interface GapPerGapUploadProps {
  gapId: string
  /** Human-readable label for the "Targeting: …" hint. */
  targetLabel: string
  /** Stub backend — the page wires this through documentService.uploadForGap. */
  onUpload: (gapId: string, files: File[]) => void
  /** Surfaces preflight rejections (size/type) to the parent. */
  onRejected?: (reason: string) => void
  busy?: boolean
}

/**
 * Drop zone scoped to a single gap. Until the backend ships
 * `targetGapId` on `POST /documents` the upload still hits the standard
 * ingestion path — the visible "Targeting: {domain}" label preserves
 * intent for the user even when the backend treats it as a generic
 * upload (DocumentService.uploadForGap currently tags via labels).
 */
export function GapPerGapUpload({
  gapId,
  targetLabel,
  onUpload,
  onRejected,
  busy = false,
}: GapPerGapUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return
      const { accepted, rejected } = partitionUploadFiles(
        Array.from(fileList)
      )
      if (rejected.length > 0) {
        onRejected?.(rejected[0].reason)
      }
      if (accepted.length > 0) {
        onUpload(gapId, accepted)
      }
    },
    [gapId, onUpload, onRejected]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault()
      setDragActive(false)
      handleFiles(e.dataTransfer.files)
    },
    [handleFiles]
  )

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragActive(true)
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        disabled={busy}
        data-testid={`gap-upload-${gapId}`}
        className={cn(
          'group flex items-center gap-3 w-full rounded-md border border-dashed px-4 py-3',
          'text-xs text-text-tertiary transition-colors text-left',
          dragActive
            ? 'border-accent bg-accent/5'
            : 'border-border-default hover:border-accent hover:bg-accent/5',
          busy && 'opacity-60 cursor-not-allowed'
        )}
      >
        <Upload
          size={14}
          className="text-accent shrink-0 group-hover:text-accent transition-colors"
        />
        <span className="flex-1 min-w-0">
          {busy
            ? 'Uploading…'
            : 'Drop a file that answers this gap — e.g. a doc or slide'}
        </span>
        <span
          data-testid={`gap-upload-target-${gapId}`}
          className="hidden sm:inline-flex shrink-0 items-center px-2 py-0.5 rounded text-[11px] font-mono text-accent bg-accent/10"
        >
          Targeting: {targetLabel}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_UPLOAD_MIME}
        className="sr-only"
        onChange={(e) => {
          handleFiles(e.target.files)
          // Reset so re-picking the same file fires onChange again.
          e.target.value = ''
        }}
      />
    </div>
  )
}
